import { MinusIcon, PackageIcon, PlusIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { Input } from '@ems/ui/components/input'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { formatMoney } from '@ems/ui/lib/format'

import { DeskSection } from '#admin/features/exhibitor/onboarding/desk-section.tsx'
import { isDecimal, type ServiceItem } from '#admin/features/exhibitor/service-orders-api.ts'

/**
 * Add-on services, ordered with the stand: power, furniture, internet. Optional. Each has a
 * stepper for whole units, and the field also takes a decimal, such as 1.5 hours.
 */
export function ServicesSection({
  services,
  currency,
  quantities,
  onQuantity,
  error,
  locked,
}: {
  /** The active services for sale in `currency`, each with its price in it. */
  services: readonly (ServiceItem & { unitPrice: number })[]
  currency: string
  quantities: Readonly<Record<string, string>>
  onQuantity: (id: string, quantity: string) => void
  error: string | undefined
  locked: boolean
}) {
  const step = (id: string, by: number) => {
    const raw = quantities[id]?.trim() ?? ''
    const now = raw !== '' && isDecimal(raw) ? Number(raw) : 0
    const next = Math.max(0, Math.floor(now) + by)
    onQuantity(id, next === 0 ? '' : String(next))
  }

  return (
    <DeskSection
      number={3}
      title="Add-on services"
      description="Optional. The services get their own invoice, with the installation fees."
      done={locked}
    >
      {services.length === 0 ? (
        <EmptyState
          icon={<PackageIcon />}
          title="No services to order"
          description={`There is no active service with a price in ${currency}.`}
        />
      ) : (
        <ul className="divide-y divide-(--border) rounded-lg border border-(--border)">
          {services.map((item) => {
            const value = quantities[item.id] ?? ''
            const invalid = value.trim() !== '' && !isDecimal(value.trim())
            return (
              <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
                <div className="flex min-w-0 flex-1 basis-48 flex-col gap-0.5">
                  <span className="text-sm font-medium">{item.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {item.category ?? 'No category'} ·{' '}
                    <span className="tabular-nums">{formatMoney(item.unitPrice, currency)}</span>{' '}
                    per {item.unit}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="size-8"
                    aria-label={`One less ${item.name}`}
                    disabled={locked || value === ''}
                    onClick={() => step(item.id, -1)}
                  >
                    <MinusIcon />
                  </Button>
                  <Input
                    aria-label={`Quantity of ${item.name}`}
                    aria-invalid={invalid}
                    inputMode="decimal"
                    placeholder="0"
                    className="h-8 w-16 text-center tabular-nums"
                    value={value}
                    disabled={locked}
                    onChange={(event) => onQuantity(item.id, event.target.value)}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="size-8"
                    aria-label={`One more ${item.name}`}
                    disabled={locked}
                    onClick={() => step(item.id, 1)}
                  >
                    <PlusIcon />
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
      {error === undefined ? null : (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
    </DeskSection>
  )
}
